// Method facts — fonte unica dei numeri con cui Coach AI compone l'alimentazione.
//
// Perché esiste: fabbisogno, macro e regole nutrizionali erano hardcoded in tre
// posti diversi (ui-profile.js per TDEE/macro, meal-recipe-composer.js per le
// regole di frequenza, nessuno per la provenienza dei dati). La pagina pubblica
// /metodo e il pannello "Perché questo piatto" devono raccontare esattamente ciò
// che il codice fa: se i numeri vivessero anche nel testo della pagina,
// diverrebbero una copia stantia al primo cambio del composer.
//
// Vincolo: questo modulo NON deve importare nulla di browser-specifico — viene
// importato anche da vite.config.js (Node) per iniettare i dati in metodo.html.

// ─── Fabbisogno energetico (usato da calcTDEE in ui-profile.js) ─────────
export const TDEE_FACTS = {
  // Cascata BMR: si usa il dato più vicino alla persona reale che abbiamo.
  bmrCascade: [
    {
      id: 'bilancia',
      label: 'Metabolismo basale misurato dalla bilancia',
      formula: 'il valore calcolato dalla tua bilancia impedenziometrica',
      when: 'quando hai caricato una misurazione con il metabolismo basale'
    },
    {
      id: 'katch-mcardle',
      label: 'Katch-McArdle',
      formula: '370 + 21,6 × massa magra (kg)',
      when: 'quando conosciamo la tua massa magra o la percentuale di grasso'
    },
    {
      id: 'mifflin',
      label: 'Mifflin-St Jeor',
      formula: 'uomo: 10×peso + 6,25×altezza − 5×età + 5 · donna: 10×peso + 6,25×altezza − 5×età − 161',
      when: 'quando abbiamo solo peso, altezza, età e sesso'
    }
  ],
  // Moltiplicatore di attività (LAF) per giorni di allenamento a settimana.
  activityFactors: [
    { maxDays: 2, factor: 1.29,  label: 'fino a 2 allenamenti a settimana' },
    { maxDays: 3, factor: 1.375, label: '3 allenamenti a settimana' },
    { maxDays: 4, factor: 1.465, label: '4 allenamenti a settimana' },
    { maxDays: 7, factor: 1.55,  label: '5 o più allenamenti a settimana' }
  ],
  // Scostamento dal fabbisogno in base all'obiettivo dichiarato.
  goalDelta: [
    { id: 'dimagrimento',  kcal: -300, label: 'Dimagrimento', why: 'deficit moderato: circa 0,3 kg a settimana, sostenibile senza perdere massa magra' },
    { id: 'mantenimento',  kcal: 0,    label: 'Mantenimento', why: 'nessuno scostamento dal fabbisogno stimato' },
    { id: 'massa',         kcal: +250, label: 'Massa',        why: 'surplus contenuto: costruisce muscolo limitando l\'accumulo di grasso' }
  ]
};

// ─── Ripartizione dei macronutrienti (usata da buildProfileFromForm) ────
export const MACRO_FACTS = {
  proteine: {
    perKgMassaMagra: 2.0,
    perKgPeso: 1.6,
    nota: 'Se conosciamo la tua massa magra usiamo 2,0 g per kg di massa magra; altrimenti 1,6 g per kg di peso corporeo.',
    fonte: 'EFSA Dietary Reference Values + posizione ISSN su fabbisogno proteico in soggetti attivi'
  },
  grassi: {
    quotaKcal: 0.30,
    nota: 'Il 30% delle calorie giornaliere arriva dai grassi, dentro il range raccomandato (20-35%).',
    fonte: 'CREA LGSA 2018 · EFSA DRV'
  },
  carboidrati: {
    nota: 'I carboidrati chiudono il conto: sono le calorie che restano dopo proteine e grassi.',
    fonte: 'calcolo a saldo'
  }
};

// ─── Fonti ufficiali citate dalle regole ───────────────────────────────
export const SOURCES = {
  'CREA LGSA 2018': 'https://www.crea.gov.it/web/alimenti-e-nutrizione/linee-guida-per-una-sana-alimentazione',
  'WHO IARC 2015 + CREA LGSA': 'https://www.iarc.who.int/featured-news/processed-meat-and-cancer/',
  'WHO IARC 2015 Group 1': 'https://www.iarc.who.int/featured-news/processed-meat-and-cancer/',
  'Mediterranean Diet Foundation': 'https://www.mdf-pyramid.net/',
  'EFSA Dietary Reference Values': 'https://www.efsa.europa.eu/en/topics/topic/dietary-reference-values'
};

// ─── Regole baseline di sana alimentazione ─────────────────────────────
// Applicate dal composer come SOFT PENALTY (riducono la probabilità che una
// ricetta venga pescata, non la bloccano) — vedi ConstraintTracker.softPenaltyFor.
// Sono override-bili dal profilo utente (profile.preferenze.limita): gli override
// utente sono HARD (bloccano via canAdd), queste restano soft.
//
// `label` è il testo mostrato all'utente; `alimento`/`regola`/`valore` sono i
// campi su cui lavora il composer. Non cambiare le chiavi senza aggiornare
// recipeContainsAlimento in meal-recipe-composer.js.
export const BASELINE_NUTRITIONAL_RULES = [
  // CREA LGSA: "max 4 porzioni/settimana, 1-2/giorno se settimana bilanciata".
  // Frittata a pranzo + cena = 2 anchor nello stesso giorno = troppo.
  { alimento: 'uova', regola: 'max_per_giorno', valore: 1, source: 'CREA LGSA 2018',
    label: 'Uova: al massimo 1 al giorno' },
  { alimento: 'uova', regola: 'max_per_settimana', valore: 4, source: 'CREA LGSA 2018',
    label: 'Uova: al massimo 4 a settimana' },
  // WHO IARC 2015 Group 2A "probably carcinogenic"; CREA raccomanda 1-2 porzioni.
  { alimento: 'carne_rossa', regola: 'max_per_settimana', valore: 2, source: 'WHO IARC 2015 + CREA LGSA',
    label: 'Carne rossa: al massimo 2 volte a settimana' },
  // WHO IARC 2015 Group 1 "carcinogenic to humans"; CREA: consumo occasionale.
  { alimento: 'salumi', regola: 'max_per_settimana', valore: 1, source: 'WHO IARC 2015 Group 1',
    label: 'Salumi e carni conservate: al massimo 1 volta a settimana' },
  // Omega-3, vitamina D, iodio. Soft minimum: bonus di peso se sotto soglia.
  { alimento: 'pesce', regola: 'min_per_settimana', valore: 2, source: 'CREA LGSA 2018',
    label: 'Pesce: almeno 2 volte a settimana' },
  { alimento: 'legumi', regola: 'min_per_settimana', valore: 2, source: 'CREA LGSA 2018',
    label: 'Legumi: almeno 2 volte a settimana' },
  // Sodio + grassi saturi. Parmigiano/grana/pecorino sono stagionati; ricotta e
  // mozzarella no.
  { alimento: 'formaggi_stagionati', regola: 'max_per_settimana', valore: 3, source: 'CREA LGSA 2018',
    label: 'Formaggi stagionati: al massimo 3 volte a settimana' },
  { alimento: 'fritti', regola: 'max_per_settimana', valore: 1, source: 'CREA LGSA 2018',
    label: 'Fritture: al massimo 1 volta a settimana' },
  { alimento: 'dolci', regola: 'max_per_settimana', valore: 3, source: 'CREA LGSA 2018',
    label: 'Dolci: al massimo 3 volte a settimana' }
];

// ─── Come si sceglie una ricetta ───────────────────────────────────────
// Quota di calorie giornaliere per pasto. Importato da generators.js
// (composeMealSkeleton) — la somma deve fare 1.
export const SLOT_RATIOS = { col: 0.25, sna1: 0.10, pra: 0.30, sna2: 0.10, cen: 0.25 };

// Finestra di ricette pescabili per un pasto, in frazione delle kcal del pasto.
// Importato da meal-recipe-composer.js (pickRecipeForSlot).
export const SLOT_KCAL_RANGE_RATIO = { min: 0.6, max: 1.5 };

const SLOT_LABELS = { col: 'Colazione', sna1: 'Spuntino 1', pra: 'Pranzo', sna2: 'Spuntino 2', cen: 'Cena' };

export const RECIPE_SELECTION_FACTS = {
  slotShare: Object.keys(SLOT_RATIOS).map(slot => ({
    slot, label: SLOT_LABELS[slot], share: SLOT_RATIOS[slot]
  })),
  kcalRange: SLOT_KCAL_RANGE_RATIO,
  varietyWindowDays: 28,
  steps: [
    'Filtriamo il ricettario tenendo solo ciò che è compatibile con le tue intolleranze, con gli alimenti che hai escluso e con le cucine che hai attivato.',
    'Per ogni pasto teniamo le ricette che stanno tra il 60% e il 150% delle calorie previste per quel pasto: così l\'adattamento resta piccolo e il piatto non si snatura.',
    'Penalizziamo le ricette che violerebbero una regola nutrizionale e quelle già viste nelle ultime 4 settimane, e favoriamo quelle che ti portano verso i minimi settimanali (pesce, legumi).',
    'Riscaliamo le quantità degli ingredienti principali sulle calorie del tuo pasto e ricalcoliamo i valori nutrizionali dai dati reali degli alimenti.'
  ]
};

// ─── Provenienza dei dati ──────────────────────────────────────────────
export const DATA_SOURCES_FACTS = [
  {
    id: 'ciqual',
    label: 'Valori nutrizionali degli alimenti',
    value: 'CIQUAL 2025 — tabella di composizione degli alimenti dell\'agenzia francese ANSES',
    url: 'https://ciqual.anses.fr/',
    why: 'Scelta su base europea: le porzioni e gli alimenti rispecchiano il modo di mangiare mediterraneo meglio di un database statunitense.'
  },
  {
    id: 'ricette',
    label: 'Ricette',
    value: 'Ricettario di piatti reali, italiani e delle altre cucine mediterranee e internazionali, normalizzati e collegati agli alimenti della tabella nutrizionale',
    why: 'Partiamo da piatti che esistono davvero, non da combinazioni di ingredienti generate a caso.'
  },
  {
    id: 'linee-guida',
    label: 'Regole di frequenza',
    value: 'CREA LGSA 2018, WHO IARC 2015, piramide della Fondazione Dieta Mediterranea, valori di riferimento EFSA',
    why: 'Le stesse linee guida usate dalla nutrizione pubblica italiana ed europea.'
  }
];

// ─── Cosa fa l'AI, e soprattutto cosa non fa ───────────────────────────
export const AI_ROLE_FACTS = {
  does: [
    'Scrive il nome del piatto, la descrizione e i passaggi di preparazione.',
    'Risponde in chat, aggiorna il profilo quando glielo chiedi e rigenera i piani.'
  ],
  doesNot: [
    'Non decide le grammature: le calcola il codice, riscalando la ricetta sulle calorie del tuo pasto.',
    'Non inventa i valori nutrizionali: calorie, proteine, carboidrati e grassi sono sommati dai dati reali degli alimenti.',
    'Non sceglie i piatti a caso: la selezione segue filtri, regole di frequenza e storico, tutti verificabili.'
  ],
  models: [
    { id: 'haiku', label: 'Claude Haiku 4.5', role: 'scrive nomi, descrizioni e preparazioni dei piatti' },
    { id: 'sonnet', label: 'Claude Sonnet 5', role: 'è il coach con cui parli in chat' }
  ]
};

// Oggetto unico esportato per l'iniezione build-time in metodo.html.
export const METHOD_FACTS = {
  tdee: TDEE_FACTS,
  macros: MACRO_FACTS,
  rules: BASELINE_NUTRITIONAL_RULES,
  sources: SOURCES,
  recipeSelection: RECIPE_SELECTION_FACTS,
  dataSources: DATA_SOURCES_FACTS,
  aiRole: AI_ROLE_FACTS
};
